import * as THREE from 'three';
import { BinaryHeap } from '../shared/binary-heap.js';
import { Points } from './points.js';
import { getLru } from '../services/config.service';
import { Profile } from './tools/profile.js';

export class ProfileData {
  profile: Profile;
  segments: {
    start: THREE.Vector3;
    end: THREE.Vector3;
    cutPlane: THREE.Plane;
    halfPlane: THREE.Plane;
    length: number;
    points: Points;
  }[];
  boundingBox = new THREE.Box3();

  constructor(profile: Profile) {
    this.profile = profile;
    this.segments = [];

    for (let i = 0; i < profile.points.length - 1; i++) {
      const start = profile.points[i];
      const end = profile.points[i + 1];

      const startGround = new THREE.Vector3(start.x, start.y, 0);
      const endGround = new THREE.Vector3(end.x, end.y, 0);

      const center = new THREE.Vector3().addVectors(endGround, startGround).multiplyScalar(0.5);
      const length = startGround.distanceTo(endGround);
      const side = new THREE.Vector3().subVectors(endGround, startGround).normalize();
      const up = new THREE.Vector3(0, 0, 1);
      const forward = new THREE.Vector3().crossVectors(side, up).normalize();
      const N = forward;
      const cutPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(N, startGround);
      const halfPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(side, center);

      this.segments.push({
        start,
        end,
        cutPlane,
        halfPlane,
        length,
        points: new Points(),
      });
    }
  }

  size(): number {
    let size = 0;
    for (const segment of this.segments) {
      size += segment.points.numPoints;
    }
    return size;
  }
}

export interface ProfileCallback {
  onProgress(data: { request: ProfileRequest; points: ProfileData }): void;
  onFinish(data: { request: ProfileRequest }): void;
  onCancel(): void;
}

export class ProfileRequest {
  pointcloud: any;
  profile: Profile;
  maxDepth: number;
  callback: ProfileCallback;
  temporaryResult: ProfileData;
  pointsServed = 0;
  highestLevelServed = 0;
  cancelRequested = false;

  priorityQueue: BinaryHeap<{ node: any; weight: number }>;
  private updateGeneratorInstance: Generator | null = null;

  constructor(
    pointcloud: any,
    profile: Profile,
    maxDepth: number | undefined,
    callback: ProfileCallback,
  ) {
    this.pointcloud = pointcloud;
    this.profile = profile;
    this.maxDepth = maxDepth || Number.MAX_VALUE;
    this.callback = callback;
    this.temporaryResult = new ProfileData(this.profile);

    this.priorityQueue = new BinaryHeap<{ node: any; weight: number }>(x => 1 / x.weight);

    this.initialize();
  }

  initialize(): void {
    this.priorityQueue.push({ node: this.pointcloud.pcoGeometry.root, weight: Infinity });
  }

  traverse(node: any): void {
    const stack: any[] = [];
    for (let i = 0; i < 8; i++) {
      const child = node.children[i];
      if (child && this.pointcloud.nodeIntersectsProfile(child, this.profile)) {
        stack.push(child);
      }
    }

    while (stack.length > 0) {
      const current = stack.pop();
      const weight = current.boundingSphere.radius;

      this.priorityQueue.push({ node: current, weight });

      if (current.level < this.maxDepth) {
        for (let i = 0; i < 8; i++) {
          const child = current.children[i];
          if (child && this.pointcloud.nodeIntersectsProfile(child, this.profile)) {
            stack.push(child);
          }
        }
      }
    }
  }

  update(): void {
    if (!this.updateGeneratorInstance) {
      this.updateGeneratorInstance = this.updateGenerator();
    }

    const result = this.updateGeneratorInstance.next();
    if (result.done) {
      this.updateGeneratorInstance = null;
    }
  }

  *updateGenerator(): Generator<boolean, void, unknown> {
    let maxNodesPerUpdate = 1;
    const intersectedNodes: any[] = [];

    for (let i = 0; i < Math.min(maxNodesPerUpdate, this.priorityQueue.size()); i++) {
      const element = this.priorityQueue.pop();
      if (!element) continue;
      const node = element.node;

      if (node.level > this.maxDepth) {
        continue;
      }
      if (node.loaded) {
        intersectedNodes.push(node);
        getLru().touch(node);
        this.highestLevelServed = Math.max(node.getLevel(), this.highestLevelServed);

        const geom = node.pcoGeometry;
        const hierarchyStepSize = geom ? geom.hierarchyStepSize : 1;

        const doTraverse =
          node.getLevel() === 0 || (node.level % hierarchyStepSize === 0 && node.hasChildren);

        if (doTraverse) {
          this.traverse(node);
        }
      } else {
        node.load();
        this.priorityQueue.push(element!);
      }
    }

    if (intersectedNodes.length > 0) {
      for (const done of this.getPointsInsideProfile(intersectedNodes, this.temporaryResult)) {
        if (!done) {
          yield false;
        }
      }
      if (this.temporaryResult.size() > 100) {
        this.pointsServed += this.temporaryResult.size();
        this.callback.onProgress({ request: this, points: this.temporaryResult });
        this.temporaryResult = new ProfileData(this.profile);
      }
    }

    if (this.priorityQueue.size() === 0) {
      if (this.temporaryResult.size() > 0) {
        this.pointsServed += this.temporaryResult.size();
        this.callback.onProgress({ request: this, points: this.temporaryResult });
        this.temporaryResult = new ProfileData(this.profile);
      }

      this.callback.onFinish({ request: this });

      const index = this.pointcloud.profileRequests.indexOf(this);
      if (index >= 0) {
        this.pointcloud.profileRequests.splice(index, 1);
      }
    }

    yield true;
  }

  *getAccepted(
    numPoints: number,
    node: any,
    matrix: THREE.Matrix4,
    segment: ProfileData['segments'][0],
    segmentDir: THREE.Vector3,
    points: Points,
    totalMileage: number,
  ): Generator<false | [Uint32Array, Float64Array, Float32Array], void, unknown> {
    let checkpoint = performance.now();

    let accepted = new Uint32Array(numPoints);
    let mileage = new Float64Array(numPoints);
    let acceptedPositions = new Float32Array(numPoints * 3);
    let numAccepted = 0;

    const pos = new THREE.Vector3();
    const svp = new THREE.Vector3();

    const view = new Float32Array(node.geometry.attributes.position.array);

    for (let i = 0; i < numPoints; i++) {
      pos.set(view[i * 3 + 0], view[i * 3 + 1], view[i * 3 + 2]);

      pos.applyMatrix4(matrix);
      const distance = Math.abs(segment.cutPlane.distanceToPoint(pos));
      const centerDistance = Math.abs(segment.halfPlane.distanceToPoint(pos));

      if (distance < this.profile.width / 2 && centerDistance < segment.length / 2) {
        svp.subVectors(pos, segment.start);
        const localMileage = segmentDir.dot(svp);

        accepted[numAccepted] = i;
        mileage[numAccepted] = localMileage + totalMileage;
        points.boundingBox.expandByPoint(pos);

        pos.sub(this.pointcloud.position);

        acceptedPositions[3 * numAccepted + 0] = pos.x;
        acceptedPositions[3 * numAccepted + 1] = pos.y;
        acceptedPositions[3 * numAccepted + 2] = pos.z;

        numAccepted++;
      }

      if (i % 1000 === 0) {
        const duration = performance.now() - checkpoint;
        if (duration > 4) {
          yield false;
          checkpoint = performance.now();
        }
      }
    }

    accepted = accepted.subarray(0, numAccepted);
    mileage = mileage.subarray(0, numAccepted);
    acceptedPositions = acceptedPositions.subarray(0, numAccepted * 3);

    yield [accepted, mileage, acceptedPositions];
  }

  *getPointsInsideProfile(nodes: any[], target: ProfileData): Generator<boolean, void, unknown> {
    let checkpoint = performance.now();
    let totalMileage = 0;

    for (const segment of target.segments) {
      for (const node of nodes) {
        const numPoints = node.numPoints;

        if (!numPoints) {
          continue;
        }

        {
          const bbWorld = node.boundingBox.clone().applyMatrix4(this.pointcloud.matrixWorld);
          const bsWorld = bbWorld.getBoundingSphere(new THREE.Sphere());

          const start = new THREE.Vector3(segment.start.x, segment.start.y, bsWorld.center.z);
          const end = new THREE.Vector3(segment.end.x, segment.end.y, bsWorld.center.z);

          const closest = new THREE.Line3(start, end).closestPointToPoint(
            bsWorld.center,
            true,
            new THREE.Vector3(),
          );
          const distance = closest.distanceTo(bsWorld.center);

          const intersects = distance < bsWorld.radius + target.profile.width;

          if (!intersects) {
            continue;
          }
        }

        const sv = new THREE.Vector3().subVectors(segment.end, segment.start).setZ(0);
        const segmentDir = sv.clone().normalize();

        const points = new Points();

        const min = node.boundingBox.min;
        const nodeMatrix = new THREE.Matrix4().makeTranslation(min.x, min.y, min.z);
        const matrix = new THREE.Matrix4().multiplyMatrices(
          this.pointcloud.matrixWorld,
          nodeMatrix,
        );

        let accepted: Uint32Array | null = null;
        let mileage: Float64Array | null = null;
        let acceptedPositions: Float32Array | null = null;

        for (const result of this.getAccepted(
          numPoints,
          node,
          matrix,
          segment,
          segmentDir,
          points,
          totalMileage,
        )) {
          if (!result) {
            const duration = performance.now() - checkpoint;
            yield false;
            checkpoint = performance.now();
          } else {
            [accepted, mileage, acceptedPositions] = result as [
              Uint32Array,
              Float64Array,
              Float32Array,
            ];
          }
        }

        const duration = performance.now() - checkpoint;
        if (duration > 4) {
          yield false;
          checkpoint = performance.now();
        }

        points.data['position'] = acceptedPositions as any;

        const relevantAttributes = Object.keys(node.geometry.attributes).filter(
          (a: string) => !['position', 'indices'].includes(a),
        );
        for (const attributeName of relevantAttributes) {
          const attribute = node.geometry.attributes[attributeName];
          const numElements = attribute.array.length / numPoints;

          const Type = attribute.array.constructor as new (len: number) => any;
          const filteredBuffer = new Type(numElements * accepted!.length);

          const source = attribute.array;

          for (let i = 0; i < accepted!.length; i++) {
            const index = accepted![i];
            const start = index * numElements;
            const end = start + numElements;
            const sub = source.subarray(start, end);
            filteredBuffer.set(sub, i * numElements);
          }

          points.data[attributeName] = filteredBuffer;
        }

        points.data['mileage'] = mileage as any;
        points.numPoints = accepted!.length;

        segment.points.add(points);
      }

      totalMileage += segment.length;
    }

    for (const segment of target.segments) {
      target.boundingBox.union(segment.points.boundingBox);
    }

    yield true;
  }

  finishLevelThenCancel(): void {
    if (this.cancelRequested) return;
    this.maxDepth = this.highestLevelServed;
    this.cancelRequested = true;
  }

  cancel(): void {
    this.callback.onCancel();

    this.priorityQueue = new BinaryHeap<{ node: any; weight: number }>(x => 1 / x.weight);

    const index = this.pointcloud.profileRequests.indexOf(this);
    if (index >= 0) {
      this.pointcloud.profileRequests.splice(index, 1);
    }
  }
}
